<template>
  <div class="user-management">
    <!-- 工具栏 -->
    <div class="toolbar">
      <el-input
        v-model="searchKeyword"
        placeholder="按用户名/名称/手机号搜索"
        clearable
        class="toolbar-input"
        @keyup.enter="refresh"
      />

      <el-button type="primary" :loading="loading" @click="refresh">刷新</el-button>
      <el-button type="success" @click="openCreateDialog">创建工单账号</el-button>
    </div>

    <!-- 用户表格 -->
    <el-table
      ref="userTableRef"
      v-loading="loading"
      :data="displayUsers"
      border
      stripe
      size="small"
      empty-text="暂无用户"
      :height="userTableHeight"
    >
      <el-table-column prop="id" label="ID" width="90" />
      <el-table-column prop="username" label="用户名" min-width="140" />
      <el-table-column label="名称" min-width="140">
        <template #default="{ row }">
          {{ getDisplayName(row) }}
        </template>
      </el-table-column>
      <el-table-column label="角色" width="140">
        <template #default="{ row }">
          {{ roleText(row) }}
        </template>
      </el-table-column>
      <el-table-column prop="phone" label="手机号" min-width="140" />
      <el-table-column label="操作" width="240" fixed="right">
        <template #default="{ row }">
          <el-button type="primary" link @click="openEditDialog(row)" :disabled="!isTechnician(row)">编辑</el-button>
          <el-button type="warning" link @click="openResetDialog(row)" :disabled="!isTechnician(row)">重置密码</el-button>
          <el-button type="danger" link @click="confirmDelete(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 创建工单账号对话框 -->
    <el-dialog v-model="createDialogVisible" title="创建工单账号" width="520px">
      <el-form ref="createFormRef" :model="createForm" :rules="createRules" label-width="90px">
        <el-form-item label="用户名" prop="username">
          <el-input v-model="createForm.username" placeholder="tech01" />
        </el-form-item>
        <el-form-item label="名称" prop="displayName">
          <el-input v-model="createForm.displayName" placeholder="例如：张三 / 售后01" />
        </el-form-item>
        <el-form-item label="密码" prop="password">
          <el-input v-model="createForm.password" type="password" placeholder="至少6位" show-password />
        </el-form-item>
        <el-form-item label="手机号" prop="phone">
          <el-input v-model="createForm.phone" placeholder="可选" />
        </el-form-item>
        <el-form-item label="角色" prop="role">
          <el-select v-model="createForm.role" style="width: 100%">
            <el-option label="售后" value="ROLE_AFTER_SALES" />
            <el-option label="维修员" value="ROLE_TECHNICIAN" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="creating" @click="handleCreate">创建</el-button>
      </template>
    </el-dialog>

    <!-- 编辑工单账号对话框 -->
    <el-dialog v-model="editDialogVisible" title="编辑工单账号" width="480px">
      <el-form ref="editFormRef" :model="editForm" :rules="editRules" label-width="90px">
        <el-form-item label="用户名" prop="username">
          <el-input v-model="editForm.username" placeholder="请输入用户名" />
        </el-form-item>
        <el-form-item label="名称" prop="displayName">
          <el-input v-model="editForm.displayName" placeholder="请输入名称" />
        </el-form-item>
        <el-form-item label="手机号" prop="phone">
          <el-input v-model="editForm.phone" placeholder="139xxxxxx" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="editing" @click="handleEdit">保存</el-button>
      </template>
    </el-dialog>

    <!-- 重置密码对话框 -->
    <el-dialog v-model="resetDialogVisible" title="重置工单账号密码" width="420px">
      <el-form ref="resetFormRef" :model="resetForm" label-width="90px">
        <el-form-item label="新密码">
          <el-input v-model="resetForm.newPassword" type="password" placeholder="至少6位" show-password />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="resetDialogVisible = false">取消</el-button>
        <el-button type="warning" :loading="resetting" @click="handleReset">重置</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick, watch } from 'vue'
import { UsersAPI, AdminTechniciansAPI } from '@/request/users'
import { lowerText, trimText } from '@/utils/text'

export default {
  name: 'UserManagement',
  setup() {
    const loading = ref(false)
    const creating = ref(false)
    const editing = ref(false)
    const resetting = ref(false)

    const searchKeyword = ref('')
    const users = ref([])

    const createDialogVisible = ref(false)
    const editDialogVisible = ref(false)
    const resetDialogVisible = ref(false)

    const createFormRef = ref(null)
    const editFormRef = ref(null)
    const resetFormRef = ref(null)

    const createForm = ref({ username: '', displayName: '', password: '', phone: '', role: 'ROLE_AFTER_SALES' })
    const editTarget = ref(null)
    const editForm = ref({ username: '', displayName: '', phone: '' })
    const resetTarget = ref(null)
    const resetForm = ref({ newPassword: '' })
    const userTableRef = ref(null)
    const userTableHeight = ref(undefined)

    const validatePhone = (rule, value, callback) => {
      const v = trimText(value)
      if (!v) return callback()
      const ok = /^1[3-9]\d{9}$/.test(v)
      if (!ok) return callback(new Error('手机号格式不正确'))
      return callback()
    }

    const createRules = {
      username: [
        { required: true, message: '请输入用户名', trigger: 'blur' },
        { min: 3, message: '至少3个字符', trigger: 'blur' }
      ],
      displayName: [
        { required: true, message: '请输入名称', trigger: 'blur' },
        { min: 2, message: '至少2个字符', trigger: 'blur' }
      ],
      password: [
        { required: true, message: '请输入密码', trigger: 'blur' },
        { min: 6, message: '至少6位', trigger: 'blur' }
      ],
      phone: [
        { validator: validatePhone, trigger: 'blur' }
      ],
      role: [
        { required: true, message: '请选择角色', trigger: 'change' }
      ]
    }

    const editRules = {
      username: [
        { required: true, message: '请输入用户名', trigger: 'blur' },
        { min: 3, message: '至少3个字符', trigger: 'blur' }
      ],
      displayName: [
        { required: true, message: '请输入名称', trigger: 'blur' },
        { min: 2, message: '至少2个字符', trigger: 'blur' }
      ],
      phone: [
        { validator: validatePhone, trigger: 'blur' }
      ]
    }

    const roleText = (row) => {
      const role = row.role || (Array.isArray(row.roles) ? row.roles[0] : '')
      const map = {
        ROLE_ADMIN: '管理员',
        ROLE_AFTER_SALES: '售后',
        ROLE_TECHNICIAN: '维修员',
        ROLE_GUEST: '访客'
      }
      return map[role] || role || '未知'
    }
    const isTechnician = (row) => {
      const role = row.role || (Array.isArray(row.roles) ? row.roles[0] : '')
      return role === 'ROLE_TECHNICIAN' || role === 'ROLE_AFTER_SALES'
    }

    const getDisplayName = (row) => {
      const raw = trimText(row?.displayName)
      return raw || row?.username || '—'
    }

    const displayUsers = computed(() => {
      const kw = lowerText(searchKeyword.value)
      const list = users.value
      if (!kw) return list
      return list.filter((u) => {
        const text = `${u.username || ''} ${getDisplayName(u)} ${u.phone || ''}`.toLowerCase()
        return text.includes(kw)
      })
    })

    const computeUserTableHeight = () => {
      nextTick(() => {
        const tableEl = userTableRef.value?.$el || userTableRef.value
        if (!tableEl) return
        const headerRow = tableEl.querySelector('.el-table__header-wrapper thead tr')
        const bodyRow = tableEl.querySelector('.el-table__body-wrapper tbody tr')
        const headerH = headerRow?.getBoundingClientRect().height || 40
        let rowH = bodyRow?.getBoundingClientRect().height || 32
        if (!isFinite(rowH) || rowH <= 0) rowH = 32
        userTableHeight.value = Math.round(headerH + rowH * 10)
      })
    }

    const fetchUsers = async () => {
      try {
        loading.value = true
        const list = await AdminTechniciansAPI.list(searchKeyword.value || '')
        users.value = Array.isArray(list) ? list : []
      } catch (err) {
        const msg = err?.message || '获取工单账号列表失败'
        if (window.ElMessage) window.ElMessage.error(msg); else console.error(msg)
      } finally {
        loading.value = false
        computeUserTableHeight()
      }
    }

    const refresh = async () => {
      await fetchUsers()
    }

    const openCreateDialog = () => {
      createForm.value = { username: '', displayName: '', password: '', phone: '', role: 'ROLE_AFTER_SALES' }
      createDialogVisible.value = true
    }
    const handleCreate = async () => {
      try {
        if (createFormRef.value) {
          const ok = await createFormRef.value.validate().catch(() => false)
          if (!ok) return
        }
        creating.value = true
        await AdminTechniciansAPI.create({
          username: trimText(createForm.value.username),
          displayName: trimText(createForm.value.displayName || createForm.value.username),
          password: createForm.value.password,
          phone: trimText(createForm.value.phone) || undefined,
          role: createForm.value.role
        })
        if (window.ElMessage) window.ElMessage.success('工单账号创建成功')
        createDialogVisible.value = false
        await refresh()
      } catch (err) {
        const msg = err?.message || '创建工单账号失败'
        if (window.ElMessage) window.ElMessage.error(msg); else console.error(msg)
      } finally {
        creating.value = false
      }
    }

    const openEditDialog = (row) => {
      editTarget.value = row
      editForm.value = {
        username: row.username || '',
        displayName: getDisplayName(row),
        phone: row.phone || ''
      }
      editDialogVisible.value = true
    }
    const handleEdit = async () => {
      try {
        if (editFormRef.value) {
          const ok = await editFormRef.value.validate().catch(() => false)
          if (!ok) return
        }
        editing.value = true
        const id = editTarget.value?.id
        await AdminTechniciansAPI.update(id, {
          username: trimText(editForm.value.username),
          displayName: trimText(editForm.value.displayName),
          phone: trimText(editForm.value.phone)
        })
        if (window.ElMessage) window.ElMessage.success('信息已更新')
        editDialogVisible.value = false
        await refresh()
      } catch (err) {
        const msg = err?.message || '更新工单账号信息失败'
        if (window.ElMessage) window.ElMessage.error(msg); else console.error(msg)
      } finally {
        editing.value = false
      }
    }

    const openResetDialog = (row) => {
      resetTarget.value = row
      resetForm.value = { newPassword: '' }
      resetDialogVisible.value = true
    }
    const handleReset = async () => {
      try {
        if (!resetForm.value.newPassword || resetForm.value.newPassword.length < 6) {
          if (window.ElMessage) window.ElMessage.warning('新密码至少6位')
          return
        }
        resetting.value = true
        const id = resetTarget.value?.id
        await AdminTechniciansAPI.resetPassword(id, resetForm.value.newPassword)
        if (window.ElMessage) window.ElMessage.success('密码已重置')
        resetDialogVisible.value = false
      } catch (err) {
        const msg = err?.message || '重置密码失败'
        if (window.ElMessage) window.ElMessage.error(msg); else console.error(msg)
      } finally {
        resetting.value = false
      }
    }

    const confirmDelete = async (row) => {
      try {
        const role = row.role || (Array.isArray(row.roles) ? row.roles[0] : '')
        const doDelete = async () => {
          if (role === 'ROLE_TECHNICIAN' || role === 'ROLE_AFTER_SALES') {
            await AdminTechniciansAPI.deleteById(row.id)
          } else {
            await UsersAPI.deleteById(row.id)
          }
        }

        if (window.ElMessageBox) {
          await window.ElMessageBox.confirm('确认删除该用户？', '提示', { type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消' })
          await doDelete()
          window.ElMessage.success('删除成功')
        } else {
          // 无全局消息组件时直接执行
          await doDelete()
          console.log('删除成功')
        }
        await refresh()
      } catch (err) {
        const msg = err?.message || '删除失败'
        if (window.ElMessage) window.ElMessage.error(msg); else console.error(msg)
      }
    }

    onMounted(() => {
      refresh()
      computeUserTableHeight()
    })

    watch(displayUsers, () => {
      computeUserTableHeight()
    })

    return {
      loading,
      creating,
      editing,
      resetting,
      searchKeyword,
      users,
      displayUsers,
      roleText,
      isTechnician,
      getDisplayName,
      refresh,
      // dialogs
      createDialogVisible,
      editDialogVisible,
      resetDialogVisible,
      openCreateDialog,
      openEditDialog,
      openResetDialog,
      handleCreate,
      handleEdit,
      handleReset,
      confirmDelete,
      createFormRef,
      editFormRef,
      resetFormRef,
      createRules,
      editRules,
      createForm,
      editForm,
      resetForm
      ,
      userTableRef,
      userTableHeight
    }
  }
}
</script>

<style scoped>
.user-management {
  padding: 8px;
}
.toolbar {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 12px;
}
.toolbar-input {
  width: 280px;
}
</style>
